import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { openExternalUrl } from '../common/utils/open-external-url';
import { isTelegramMiniApp } from '../common/utils/telegram-mini-app';
import { CLIENT_WALLET_TOPUP_DEEP_LINK_PATH } from './deep-link.service';

/** Pending wallet top-up intent while payment Custom Tab is open. */
export const PENDING_PAYMENT_INTENT_STORAGE_KEY =
  'nebalkon.pendingPaymentIntent';

@Injectable({ providedIn: 'root' })
export class PaymentBrowserService {
  readonly #router = inject(Router);
  readonly #zone = inject(NgZone);
  readonly #destroyRef = inject(DestroyRef);
  readonly #document = inject(DOCUMENT);
  readonly #platformId = inject(PLATFORM_ID);

  constructor() {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    void (async (): Promise<void> => {
      const handle = await Browser.addListener('browserFinished', () => {
        this.#zone.run(() => {
          this.#handleBrowserFinished();
        });
      });
      this.#destroyRef.onDestroy(() => void handle.remove());
    })();
  }

  savePendingIntentId(intentId: string): void {
    const normalized = intentId.trim();
    if (!normalized || !this.#canUseSessionStorage()) {
      return;
    }

    this.#sessionStorage()?.setItem(
      PENDING_PAYMENT_INTENT_STORAGE_KEY,
      normalized,
    );
  }

  peekPendingIntentId(): string | null {
    if (!this.#canUseSessionStorage()) {
      return null;
    }

    const raw = this.#sessionStorage()?.getItem(
      PENDING_PAYMENT_INTENT_STORAGE_KEY,
    );
    return raw?.trim() || null;
  }

  consumePendingIntentId(): string | null {
    const intentId = this.peekPendingIntentId();
    this.clearPendingIntentId();
    return intentId;
  }

  clearPendingIntentId(): void {
    if (!this.#canUseSessionStorage()) {
      return;
    }

    this.#sessionStorage()?.removeItem(PENDING_PAYMENT_INTENT_STORAGE_KEY);
  }

  async openPaymentUrl(formUrl: string, intentId?: string): Promise<void> {
    const url = formUrl.trim();
    if (!url) {
      return;
    }

    if (intentId?.trim()) {
      this.savePendingIntentId(intentId);
    }

    if (isTelegramMiniApp()) {
      const webApp = window.Telegram?.WebApp;
      const view = this.#document.defaultView;

      if (view) {
        view.location.assign(url);
        return;
      }

      if (webApp?.openLink) {
        webApp.openLink(url, { try_instant_view: false });
        return;
      }

      throw new Error('cannot open payment url in telegram mini app');
    }

    await openExternalUrl(url);
  }

  #handleBrowserFinished(): void {
    const intentId = this.consumePendingIntentId();
    if (!intentId) {
      return;
    }

    if (this.#currentIntentQueryParam() === intentId) {
      return;
    }

    void this.#router.navigateByUrl(
      `${CLIENT_WALLET_TOPUP_DEEP_LINK_PATH}?intent=${encodeURIComponent(intentId)}`,
    );
  }

  #currentIntentQueryParam(): string {
    const params = this.#router.parseUrl(this.#router.url).queryParams;
    const raw = params['intent'];
    return typeof raw === 'string' ? raw.trim() : '';
  }

  #canUseSessionStorage(): boolean {
    return isPlatformBrowser(this.#platformId) && !!this.#sessionStorage();
  }

  #sessionStorage(): Storage | null {
    return this.#document.defaultView?.sessionStorage ?? null;
  }
}
